import { useState } from "react";
import { Calculator } from "lucide-react";
import { PageHeader, Panel } from "@/components/layout/AppShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useRx } from "@/context/AuthContext";
import { fmt } from "@/lib/format";

const FIELDS = [
  ["capital", "Trading capital (₹)", 500000], ["risk_pct", "Risk per trade (%)", 1], ["entry", "Entry price", 24500],
  ["stop_loss", "Stop loss", 24420], ["target", "Target (optional)", 24660], ["lot_size", "Lot size", 75],
];

export default function RiskCalculatorPage() {
  const { api } = useRx();
  const [f, setF] = useState(Object.fromEntries(FIELDS.map(([k, , v]) => [k, v])));
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);
  const calc = async (e) => {
    e.preventDefault();
    setErr(null);
    const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v === "" ? null : Number(v)]));
    try { setRes(await api("post", "/tools/risk", body)); }
    catch (x) { setErr(typeof x.response?.data?.detail === "string" ? x.response.data.detail : "Check the inputs"); setRes(null); }
  };
  const out = res && [
    ["Direction", res.direction], ["Risk amount", `₹${fmt(res.risk_amount)}`], ["Risk / unit", fmt(res.risk_per_unit)],
    ["Lots", res.lots], ["Position qty", res.position_qty], ["Capital required", `₹${fmt(res.capital_required)}`],
    ["Actual risk", `₹${fmt(res.actual_risk)}`], ["Potential reward", res.potential_reward != null ? `₹${fmt(res.potential_reward)}` : "—"],
    ["Risk : Reward", res.risk_reward != null ? `1 : ${res.risk_reward}` : "—"],
  ];
  return (
    <div data-testid="risk-page">
      <PageHeader eyebrow="Tools" title="Risk Calculator" sub="Size every position from your capital and max risk. Works for options, futures, stocks and commodities." />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Inputs">
          <form onSubmit={calc} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="risk-form">
            {FIELDS.map(([k, l]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-xs text-gray-400">{l}</Label>
                <Input type="number" step="any" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`risk-input-${k}`}
                  className="bg-black/40 border-white/10 font-data focus-visible:ring-[#007AFF]" />
              </div>
            ))}
            <button type="submit" data-testid="risk-calc-btn" className="sm:col-span-2 mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#00FF66] text-black font-bold py-2.5 hover:brightness-110 transition-[filter]">
              <Calculator size={16} /> Calculate position
            </button>
            {err && <div className="sm:col-span-2 text-sm text-[#FF3366]" data-testid="risk-error">{err}</div>}
          </form>
        </Panel>
        <Panel title="Result" testId="risk-result">
          {!res ? <div className="text-sm text-gray-500 py-10 text-center">Enter values and calculate.</div> : (
            <div className="grid grid-cols-2 gap-3">
              {out.map(([k, v]) => (
                <div key={k} className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500">{k}</div>
                  <div className="font-data text-lg font-bold mt-1" data-testid={`risk-out-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v}</div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
